/**
 * Generated study data: zod schemas with inferred TypeScript types.
 *
 * The generator in data/ emits one JSON file per study matching StudyFileSchema plus a
 * manifest matching ManifestSchema. The schema test in data/test validates every generated
 * file against these schemas, and the server validates at boot. Dashboard summaries are
 * precomputed here so the client never aggregates raw records at runtime; it only looks
 * records up by id.
 */
import { z } from 'zod';
import { QuerySchema } from './query.ts';

export const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
  message: 'Expected YYYY-MM-DD',
});

/* ---------- Study definition ---------- */

export const STUDY_CODES = ['ONC-2041', 'CARD-317', 'NEURO-089'] as const;
export const StudyCodeSchema = z.enum(STUDY_CODES);
export type StudyCode = z.infer<typeof StudyCodeSchema>;

export const FORM_NAMES = [
  'Demographics',
  'Vitals',
  'Physical Exam',
  'Adverse Events',
  'Concomitant Medications',
  'Labs',
  'ECG',
  'Tumour Assessment',
  'EDSS',
  'MRI',
  'Dosing',
] as const;
export const FormNameSchema = z.enum(FORM_NAMES);
export type FormName = z.infer<typeof FormNameSchema>;

export const VisitDefinitionSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
  /** Study day relative to first dose / baseline (day 0). Screening is negative. */
  targetDay: z.number().int(),
  windowBefore: z.number().int().min(0),
  windowAfter: z.number().int().min(0),
  forms: z.array(FormNameSchema),
});
export type VisitDefinition = z.infer<typeof VisitDefinitionSchema>;

export const FormDefinitionSchema = z.object({
  name: FormNameSchema,
  fields: z.array(z.string().min(1)).min(1),
});
export type FormDefinition = z.infer<typeof FormDefinitionSchema>;

export const ArmSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  ratio: z.number().positive(),
});
export type Arm = z.infer<typeof ArmSchema>;

export const StudyInfoSchema = z.object({
  code: StudyCodeSchema,
  title: z.string().min(1),
  shortTitle: z.string().min(1),
  phase: z.enum(['I', 'II', 'III', 'IV']),
  therapeuticArea: z.string().min(1),
  indication: z.string().min(1),
  intervention: z.string().min(1),
  design: z.string().min(1),
  blinded: z.boolean(),
  arms: z.array(ArmSchema).min(1),
  /** Hex colour used for the room accent, signage and logo tint. */
  accentColour: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  sponsor: z.string().min(1),
  startDate: IsoDateSchema,
  /** The data cut (anchor) date everything is computed as of. */
  dataCutDate: IsoDateSchema,
  enrollmentTarget: z.number().int().positive(),
  /** Planned enrollment per month used for the planned curve. */
  plannedEnrollmentPerMonth: z.number().positive(),
  visitSchedule: z.array(VisitDefinitionSchema).min(1),
  forms: z.array(FormDefinitionSchema).min(1),
});
export type StudyInfo = z.infer<typeof StudyInfoSchema>;

/* ---------- Sites and subjects ---------- */

export const SiteSchema = z.object({
  id: z.string().regex(/^\d{4}$/),
  name: z.string().min(1),
  city: z.string().min(1),
  country: z.string().min(1),
  countryCode: z.string().length(2),
  piName: z.string().min(1),
  activationDate: IsoDateSchema,
  enrollmentTarget: z.number().int().positive(),
  /** Position on the stylised risk map, normalised 0..1. */
  mapX: z.number().min(0).max(1),
  mapY: z.number().min(0).max(1),
});
export type Site = z.infer<typeof SiteSchema>;

export const SUBJECT_STATUSES = [
  'Screening',
  'Screen Failed',
  'Enrolled',
  'On Treatment',
  'Completed',
  'Discontinued',
  'Lost to Follow-up',
] as const;
export const SubjectStatusSchema = z.enum(SUBJECT_STATUSES);
export type SubjectStatus = z.infer<typeof SubjectStatusSchema>;

export const BMI_BUCKETS = ['Underweight', 'Normal', 'Overweight', 'Obese'] as const;
export const BmiBucketSchema = z.enum(BMI_BUCKETS);
export type BmiBucket = z.infer<typeof BmiBucketSchema>;

export const SUBJECT_FLAGS = [
  'SAE',
  'SAE last 30 days',
  'Grade 3+ lab',
  "Hy's law candidate",
  'Major deviation',
  'Missed visit',
  'Open queries',
] as const;
export const SubjectFlagSchema = z.enum(SUBJECT_FLAGS);
export type SubjectFlag = z.infer<typeof SubjectFlagSchema>;

export const SubjectSchema = z.object({
  /** SSSS-NNNN, site id then sequence. */
  id: z.string().regex(/^\d{4}-\d{4}$/),
  siteId: z.string().regex(/^\d{4}$/),
  consentDate: IsoDateSchema,
  screeningDate: IsoDateSchema,
  randomisationDate: IsoDateSchema.optional(),
  firstDoseDate: IsoDateSchema.optional(),
  arm: z.string().optional(),
  sex: z.enum(['F', 'M']),
  age: z.number().int().min(18).max(95),
  heightCm: z.number().min(130).max(210),
  weightKg: z.number().min(35).max(200),
  bmi: z.number().min(12).max(60),
  bmiBucket: BmiBucketSchema,
  /** Present for CRF fidelity, never used for mannequin appearance. */
  race: z.string().min(1),
  ethnicity: z.string().min(1),
  status: SubjectStatusSchema,
  statusDate: IsoDateSchema,
  discontinuationReason: z.string().optional(),
  discontinuationDate: IsoDateSchema.optional(),
  screenFailReason: z.string().optional(),
  flags: z.array(SubjectFlagSchema),
});
export type Subject = z.infer<typeof SubjectSchema>;

/* ---------- Visits and forms ---------- */

export const VISIT_STATUSES = ['Completed', 'Missed', 'Scheduled', 'Not Applicable'] as const;
export const VisitStatusSchema = z.enum(VISIT_STATUSES);
export type VisitStatus = z.infer<typeof VisitStatusSchema>;

export const VISIT_TIMINGS = ['On time', 'Early', 'Late', 'Missed', 'Pending'] as const;
export const VisitTimingSchema = z.enum(VISIT_TIMINGS);
export type VisitTiming = z.infer<typeof VisitTimingSchema>;

export const VisitSchema = z.object({
  id: z.string().min(1),
  subjectId: z.string().min(1),
  siteId: z.string().min(1),
  visitCode: z.string().min(1),
  visitName: z.string().min(1),
  targetDay: z.number().int(),
  targetDate: IsoDateSchema,
  windowStart: IsoDateSchema,
  windowEnd: IsoDateSchema,
  actualDate: IsoDateSchema.optional(),
  status: VisitStatusSchema,
  timing: VisitTimingSchema,
  /** actual - target in days; negative is early. */
  deviationDays: z.number().int().optional(),
  missedReason: z.string().optional(),
});
export type Visit = z.infer<typeof VisitSchema>;

export const FORM_STATUSES = ['Not Started', 'Entered', 'Verified', 'Signed', 'Locked'] as const;
export const FormStatusSchema = z.enum(FORM_STATUSES);
export type FormStatus = z.infer<typeof FormStatusSchema>;

/** One CRF page: a form instance for a subject at a visit. */
export const FormRecordSchema = z.object({
  id: z.string().min(1),
  subjectId: z.string().min(1),
  siteId: z.string().min(1),
  visitId: z.string().min(1),
  visitCode: z.string().min(1),
  form: FormNameSchema,
  status: FormStatusSchema,
  totalFields: z.number().int().positive(),
  missingFields: z.array(z.string()),
  dueDate: IsoDateSchema,
  overdue: z.boolean(),
  enteredAt: IsoDateSchema.optional(),
  verifiedAt: IsoDateSchema.optional(),
  signedAt: IsoDateSchema.optional(),
  lockedAt: IsoDateSchema.optional(),
});
export type FormRecord = z.infer<typeof FormRecordSchema>;

/* ---------- Clinical records ---------- */

export const LAB_PANELS = ['Haematology', 'Chemistry'] as const;
export const LabPanelSchema = z.enum(LAB_PANELS);

export const LabSchema = z.object({
  id: z.string().min(1),
  subjectId: z.string().min(1),
  siteId: z.string().min(1),
  visitId: z.string().min(1),
  visitCode: z.string().min(1),
  date: IsoDateSchema,
  panel: LabPanelSchema,
  analyte: z.string().min(1),
  value: z.number(),
  unit: z.string().min(1),
  low: z.number(),
  high: z.number(),
  abnormalFlag: z.enum(['L', 'H']).nullable(),
  /** CTCAE-style grade, 0 = normal. */
  grade: z.number().int().min(0).max(4),
  /** value / upper limit of normal, for liver analytes. */
  ulnMultiple: z.number().optional(),
  /** Clinical significance review; null when the value is normal. */
  clinicallySignificant: z.enum(['Yes', 'No', 'Pending']).nullable(),
});
export type Lab = z.infer<typeof LabSchema>;

export const BODY_REGIONS = [
  'Head',
  'Chest',
  'Abdomen',
  'Left arm',
  'Right arm',
  'Left leg',
  'Right leg',
  'Skin',
  'Systemic',
] as const;
export const BodyRegionSchema = z.enum(BODY_REGIONS);
export type BodyRegion = z.infer<typeof BodyRegionSchema>;

export const SERIOUSNESS_CRITERIA = [
  'Death',
  'Life-threatening',
  'Hospitalisation',
  'Disability',
  'Congenital anomaly',
  'Other medically important',
] as const;
export const SeriousnessCriterionSchema = z.enum(SERIOUSNESS_CRITERIA);
export type SeriousnessCriterion = z.infer<typeof SeriousnessCriterionSchema>;

export const RELATEDNESS = [
  'Not related',
  'Unlikely',
  'Possibly',
  'Probably',
  'Definitely',
] as const;
export const RelatednessSchema = z.enum(RELATEDNESS);
export type Relatedness = z.infer<typeof RelatednessSchema>;

export const AE_ACTIONS = [
  'None',
  'Dose reduced',
  'Drug interrupted',
  'Drug withdrawn',
  'Not applicable',
] as const;
export const AeActionSchema = z.enum(AE_ACTIONS);

export const AE_OUTCOMES = [
  'Recovered',
  'Recovering',
  'Not recovered',
  'Recovered with sequelae',
  'Fatal',
  'Unknown',
] as const;
export const AeOutcomeSchema = z.enum(AE_OUTCOMES);

export const AdverseEventSchema = z.object({
  id: z.string().min(1),
  subjectId: z.string().min(1),
  siteId: z.string().min(1),
  /** MedDRA-style preferred term. */
  term: z.string().min(1),
  /** System organ class. */
  soc: z.string().min(1),
  bodyRegion: BodyRegionSchema,
  onsetDate: IsoDateSchema,
  resolutionDate: IsoDateSchema.optional(),
  ongoing: z.boolean(),
  grade: z.number().int().min(1).max(5),
  serious: z.boolean(),
  seriousnessCriteria: z.array(SeriousnessCriterionSchema),
  relatedness: RelatednessSchema,
  expected: z.boolean(),
  actionTaken: AeActionSchema,
  outcome: AeOutcomeSchema,
  reportedDate: IsoDateSchema,
  /** Study day of onset relative to first dose, when dosed. */
  onsetStudyDay: z.number().int().optional(),
  narrative: z.string().optional(),
});
export type AdverseEvent = z.infer<typeof AdverseEventSchema>;

export const ConMedSchema = z.object({
  id: z.string().min(1),
  subjectId: z.string().min(1),
  siteId: z.string().min(1),
  drug: z.string().min(1),
  indication: z.string().min(1),
  dose: z.number().positive(),
  unit: z.string().min(1),
  route: z.string().min(1),
  frequency: z.string().min(1),
  startDate: IsoDateSchema,
  endDate: IsoDateSchema.optional(),
  ongoing: z.boolean(),
});
export type ConMed = z.infer<typeof ConMedSchema>;

export const DEVIATION_CATEGORIES = [
  'Eligibility',
  'Dosing',
  'Visit window',
  'Consent',
  'Prohibited medication',
  'Procedure',
] as const;
export const DeviationCategorySchema = z.enum(DEVIATION_CATEGORIES);
export type DeviationCategory = z.infer<typeof DeviationCategorySchema>;

export const DeviationSchema = z.object({
  id: z.string().min(1),
  subjectId: z.string().min(1),
  siteId: z.string().min(1),
  visitId: z.string().optional(),
  visitCode: z.string().optional(),
  category: DeviationCategorySchema,
  classification: z.enum(['Major', 'Minor']).nullable(),
  date: IsoDateSchema,
  reportedDate: IsoDateSchema,
  description: z.string().min(1),
  correctiveAction: z.string().optional(),
  status: z.enum(['Open', 'Resolved']),
});
export type Deviation = z.infer<typeof DeviationSchema>;

export const DosingSchema = z.object({
  id: z.string().min(1),
  subjectId: z.string().min(1),
  siteId: z.string().min(1),
  visitId: z.string().min(1),
  visitCode: z.string().min(1),
  date: IsoDateSchema,
  drug: z.string().min(1),
  dose: z.number().nonnegative(),
  unit: z.string().min(1),
  administered: z.boolean(),
  /** Doses missed in the period covered by this record. */
  missedDoses: z.number().int().nonnegative(),
  reason: z.string().optional(),
});
export type Dosing = z.infer<typeof DosingSchema>;

export const VitalsSchema = z.object({
  id: z.string().min(1),
  subjectId: z.string().min(1),
  siteId: z.string().min(1),
  visitId: z.string().min(1),
  visitCode: z.string().min(1),
  date: IsoDateSchema,
  systolicBp: z.number().int(),
  diastolicBp: z.number().int(),
  heartRate: z.number().int(),
  temperatureC: z.number(),
  respiratoryRate: z.number().int(),
  weightKg: z.number(),
});
export type Vitals = z.infer<typeof VitalsSchema>;

export const EcgSchema = z.object({
  id: z.string().min(1),
  subjectId: z.string().min(1),
  siteId: z.string().min(1),
  visitId: z.string().min(1),
  visitCode: z.string().min(1),
  date: IsoDateSchema,
  heartRate: z.number().int(),
  prMs: z.number().int(),
  qrsMs: z.number().int(),
  qtMs: z.number().int(),
  qtcfMs: z.number().int(),
  interpretation: z.enum([
    'Normal',
    'Abnormal, not clinically significant',
    'Abnormal, clinically significant',
  ]),
});
export type Ecg = z.infer<typeof EcgSchema>;

/* ---------- Risk-based monitoring ---------- */

export const KRI_IDS = [
  'queryRate',
  'saeRate',
  'deviationRate',
  'missingDataPct',
  'visitLatenessPct',
  'enrollmentPace',
] as const;
export const KriIdSchema = z.enum(KRI_IDS);
export type KriId = z.infer<typeof KriIdSchema>;

export const KRI_LABELS: Record<KriId, string> = {
  queryRate: 'Query rate (per 100 pages)',
  saeRate: 'SAE rate (per 100 subjects)',
  deviationRate: 'Deviations per subject',
  missingDataPct: 'Missing data (%)',
  visitLatenessPct: 'Visits late or missed (%)',
  enrollmentPace: 'Enrollment pace (% of plan)',
};

export const KriValueSchema = z.object({
  value: z.number(),
  unit: z.string(),
  /** Normalised 0..100 risk contribution. */
  risk: z.number().min(0).max(100),
  threshold: z.number(),
  breached: z.boolean(),
});
export type KriValue = z.infer<typeof KriValueSchema>;

export const RagSchema = z.enum(['Green', 'Amber', 'Red']);
export type Rag = z.infer<typeof RagSchema>;

export const SiteRiskSchema = z.object({
  siteId: z.string().min(1),
  score: z.number().min(0).max(100),
  rag: RagSchema,
  krisBreached: z.number().int().nonnegative(),
  kris: z.object({
    queryRate: KriValueSchema,
    saeRate: KriValueSchema,
    deviationRate: KriValueSchema,
    missingDataPct: KriValueSchema,
    visitLatenessPct: KriValueSchema,
    enrollmentPace: KriValueSchema,
  }),
  /** 12 weekly scores, oldest first, ending at the data cut. */
  history: z
    .array(z.object({ weekEnding: IsoDateSchema, score: z.number().min(0).max(100) }))
    .length(12),
  /** Score delta over the history window. */
  trend: z.number(),
});
export type SiteRisk = z.infer<typeof SiteRiskSchema>;

export const RbmSchema = z.object({
  computedAt: IsoDateSchema,
  weights: z.object({
    queryRate: z.number(),
    saeRate: z.number(),
    deviationRate: z.number(),
    missingDataPct: z.number(),
    visitLatenessPct: z.number(),
    enrollmentPace: z.number(),
  }),
  thresholds: z.object({ amber: z.number(), red: z.number() }),
  sites: z.array(SiteRiskSchema),
});
export type Rbm = z.infer<typeof RbmSchema>;

/* ---------- Dashboard summaries ---------- */

const SiteCount = z.object({ siteId: z.string(), count: z.number().int().nonnegative() });

export const EnrollmentDashboardSchema = z.object({
  kpis: z.object({
    enrolled: z.number().int(),
    target: z.number().int(),
    screened: z.number().int(),
    screenFailed: z.number().int(),
    screenFailureRate: z.number(),
    medianDaysConsentToRandomisation: z.number().nullable(),
  }),
  /** Weekly cumulative actual vs planned, oldest first. */
  cumulative: z.array(
    z.object({ weekEnding: IsoDateSchema, actual: z.number().int(), planned: z.number() }),
  ),
  perSite: z.array(
    z.object({
      siteId: z.string(),
      screened: z.number().int(),
      screenFailed: z.number().int(),
      enrolled: z.number().int(),
      target: z.number().int(),
      subjectIds: z.array(z.string()),
    }),
  ),
});

export const SubjectStatusDashboardSchema = z.object({
  counts: z.record(SubjectStatusSchema, z.number().int()),
  /** Sankey links from screening to final status. */
  flow: z.array(z.object({ from: z.string(), to: z.string(), count: z.number().int() })),
  perSite: z.array(
    z.object({ siteId: z.string(), counts: z.record(SubjectStatusSchema, z.number().int()) }),
  ),
  subjectIdsByStatus: z.record(SubjectStatusSchema, z.array(z.string())),
});

export const VisitComplianceDashboardSchema = z.object({
  kpis: z.object({
    dueVisits: z.number().int(),
    onTimePct: z.number(),
    earlyPct: z.number(),
    latePct: z.number(),
    missedPct: z.number(),
    meanDeviationDays: z.number(),
  }),
  perSite: z.array(
    z.object({
      siteId: z.string(),
      onTime: z.number().int(),
      early: z.number().int(),
      late: z.number().int(),
      missed: z.number().int(),
      total: z.number().int(),
    }),
  ),
  /** Visit ids per subject in schedule order, for the Gantt view. */
  gantt: z.array(z.object({ subjectId: z.string(), visitIds: z.array(z.string()) })),
});

export const MissingDataDashboardSchema = z.object({
  kpis: z.object({
    missingFieldsPct: z.number(),
    missingFields: z.number().int(),
    expectedFields: z.number().int(),
    formsNotStarted: z.number().int(),
    overdueForms: z.number().int(),
  }),
  heatmap: z.object({
    visits: z.array(z.string()),
    forms: z.array(FormNameSchema),
    cells: z.array(
      z.object({
        visitCode: z.string(),
        form: FormNameSchema,
        missingPct: z.number(),
        missingFields: z.number().int(),
        expectedFields: z.number().int(),
        /** Form record ids with at least one missing field or not started. */
        formIds: z.array(z.string()),
      }),
    ),
  }),
  worstSites: z.array(
    z.object({ siteId: z.string(), missingPct: z.number(), overdueForms: z.number().int() }),
  ),
  overdueBySite: z.array(SiteCount),
});

export const QUERY_AGE_BUCKETS = ['0-7', '8-14', '15-30', '30+'] as const;
export const QueryAgeBucketSchema = z.enum(QUERY_AGE_BUCKETS);

export const QueryManagementDashboardSchema = z.object({
  kpis: z.object({
    open: z.number().int(),
    answered: z.number().int(),
    closed: z.number().int(),
    medianDaysOpen: z.number().nullable(),
    crfPages: z.number().int(),
    queriesPer100Pages: z.number(),
  }),
  aging: z.array(
    z.object({
      bucket: QueryAgeBucketSchema,
      total: z.number().int(),
      bySite: z.array(SiteCount),
      queryIds: z.array(z.string()),
    }),
  ),
  /** Open queries at the end of each of the last 12 weeks. */
  trend: z.array(z.object({ weekEnding: IsoDateSchema, open: z.number().int() })),
});

export const DataCleaningDashboardSchema = z.object({
  kpis: z.object({
    cleanPct: z.number(),
    sdvPct: z.number(),
    signedPct: z.number(),
    lockedPct: z.number(),
    daysSinceLastEntry: z.number().int().nullable(),
    totalForms: z.number().int(),
  }),
  funnel: z.array(
    z.object({
      form: FormNameSchema,
      total: z.number().int(),
      entered: z.number().int(),
      verified: z.number().int(),
      signed: z.number().int(),
      locked: z.number().int(),
      /** Form record ids needing attention: not started or entered with missing fields. */
      attentionFormIds: z.array(z.string()),
    }),
  ),
  progress: z.array(
    z.object({
      weekEnding: IsoDateSchema,
      enteredPct: z.number(),
      verifiedPct: z.number(),
      signedPct: z.number(),
      lockedPct: z.number(),
    }),
  ),
});

export const ProtocolDeviationDashboardSchema = z.object({
  kpis: z.object({
    total: z.number().int(),
    major: z.number().int(),
    minor: z.number().int(),
    unclassified: z.number().int(),
  }),
  byCategory: z.array(
    z.object({
      category: DeviationCategorySchema,
      count: z.number().int(),
      major: z.number().int(),
      minor: z.number().int(),
      deviationIds: z.array(z.string()),
    }),
  ),
  bySite: z.array(SiteCount),
  top5: z.array(z.object({ category: DeviationCategorySchema, count: z.number().int() })),
});

export const LabAbnormalityDashboardSchema = z.object({
  kpis: z.object({
    subjectsWithGrade3Plus: z.number().int(),
    hysLawCandidates: z.number().int(),
    pendingCsReview: z.number().int(),
  }),
  /** Worst ALT vs bilirubin per subject, in ULN multiples. */
  scatter: z.array(
    z.object({
      subjectId: z.string(),
      altUln: z.number(),
      biliUln: z.number(),
      altLabId: z.string(),
      biliLabId: z.string(),
      visitCode: z.string(),
      hysLaw: z.boolean(),
    }),
  ),
  gradeDistribution: z.array(
    z.object({ analyte: z.string(), grades: z.array(z.number().int()).length(5) }),
  ),
  hysLawSubjectIds: z.array(z.string()),
  grade3PlusSubjectIds: z.array(z.string()),
});

export const SaeReviewDashboardSchema = z.object({
  kpis: z.object({
    total: z.number().int(),
    unexpected: z.number().int(),
    related: z.number().int(),
    fatal: z.number().int(),
    saesPer100Subjects: z.number(),
  }),
  timeline: z.array(
    z.object({
      aeId: z.string(),
      subjectId: z.string(),
      siteId: z.string(),
      term: z.string(),
      onsetDate: IsoDateSchema,
      /** Days since study start, for placement along the wall. */
      studyDayFromStart: z.number().int(),
      criteria: z.array(SeriousnessCriterionSchema),
      grade: z.number().int(),
      relatedness: RelatednessSchema,
      expected: z.boolean(),
    }),
  ),
  /** Severity (grade) vs relatedness grid counts. */
  grid: z.array(
    z.object({ grade: z.number().int(), relatedness: RelatednessSchema, count: z.number().int() }),
  ),
});

export const RbmDashboardSchema = z.object({
  kpis: z.object({
    sitesRed: z.number().int(),
    sitesAmber: z.number().int(),
    sitesGreen: z.number().int(),
    krisBreached: z.number().int(),
    meanScore: z.number(),
  }),
});

export const PatientProfilesDashboardSchema = z.object({
  kpis: z.object({
    subjectsInStudy: z.number().int(),
    flaggedSubjects: z.number().int(),
    averageAge: z.number(),
    sexSplit: z.object({ F: z.number().int(), M: z.number().int() }),
  }),
  cards: z.array(
    z.object({
      subjectId: z.string(),
      siteId: z.string(),
      arm: z.string().optional(),
      status: SubjectStatusSchema,
      age: z.number().int(),
      sex: z.enum(['F', 'M']),
      flags: z.array(SubjectFlagSchema),
      openQueries: z.number().int(),
      saeLast30Days: z.boolean(),
      visitsCompleted: z.number().int(),
      visitsDue: z.number().int(),
    }),
  ),
});

export const DashboardsSchema = z.object({
  header: z.object({
    enrolled: z.number().int(),
    target: z.number().int(),
    openQueries: z.number().int(),
    lastDataCut: IsoDateSchema,
  }),
  enrollment: EnrollmentDashboardSchema,
  subjectStatus: SubjectStatusDashboardSchema,
  visitCompliance: VisitComplianceDashboardSchema,
  missingData: MissingDataDashboardSchema,
  queryManagement: QueryManagementDashboardSchema,
  dataCleaning: DataCleaningDashboardSchema,
  protocolDeviation: ProtocolDeviationDashboardSchema,
  labAbnormality: LabAbnormalityDashboardSchema,
  saeReview: SaeReviewDashboardSchema,
  rbm: RbmDashboardSchema,
  patientProfiles: PatientProfilesDashboardSchema,
});
export type Dashboards = z.infer<typeof DashboardsSchema>;

/* ---------- Study file and manifest ---------- */

export const StudyFileSchema = z.object({
  study: StudyInfoSchema,
  generatedAt: z.string(),
  seed: z.string(),
  anchorDate: IsoDateSchema,
  sites: z.array(SiteSchema).min(1),
  subjects: z.array(SubjectSchema).min(1),
  visits: z.array(VisitSchema),
  forms: z.array(FormRecordSchema),
  labs: z.array(LabSchema),
  adverseEvents: z.array(AdverseEventSchema),
  conMeds: z.array(ConMedSchema),
  deviations: z.array(DeviationSchema),
  dosing: z.array(DosingSchema),
  vitals: z.array(VitalsSchema),
  ecgs: z.array(EcgSchema),
  rbm: RbmSchema,
  dashboards: DashboardsSchema,
  seedQueries: z.array(QuerySchema),
});
export type StudyFile = z.infer<typeof StudyFileSchema>;

export const ManifestStudySchema = z.object({
  code: StudyCodeSchema,
  title: z.string(),
  shortTitle: z.string(),
  phase: z.string(),
  therapeuticArea: z.string(),
  file: z.string(),
  siteCount: z.number().int(),
  subjectCount: z.number().int(),
  enrolled: z.number().int(),
  target: z.number().int(),
  accentColour: z.string(),
  seedQueryCount: z.number().int(),
});

export const ManifestSchema = z.object({
  generatedAt: z.string(),
  seed: z.string(),
  anchorDate: IsoDateSchema,
  // The lobby scales to many studies (the generator ships three); see docs/lobby-redesign.md.
  studies: z.array(ManifestStudySchema).min(1).max(60),
});
export type Manifest = z.infer<typeof ManifestSchema>;
